import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import apiFetch from "../utils/apiFetch";
import useDebounce from "../hooks/useDebounce";
import UserContext from "../hooks/UserContext";
import DeletedLogModal from "../components/substation/DeletedLogModal";
import PageSwitcher from "../components/substation/PageSwitcher";

const VOLTAGES = ["500", "220", "110", "35"];
const PAGE_SIZE = 30;

export default function NetworkList() {
  const { permissions } = useContext(UserContext);
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  const [search, setSearch] = useState("");
  const [voltage, setVoltage] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const debouncedSearch = useDebounce(search, 300);

  const canCreate = permissions.includes("NETWORK_CREATE");
  const hasFilters = Boolean(search || voltage);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, voltage]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams();
        params.set("page", page);
        params.set("limit", PAGE_SIZE);
        if (debouncedSearch) params.set("search", debouncedSearch);
        if (voltage) params.set("voltage", voltage);
        const res = await apiFetch(`/api/network-management?${params}`);
        if (!res.ok) throw new Error("NETWORKS_LOAD_FAILED");
        const data = await res.json();
        if (!cancelled) {
          setRows(data.rows || []);
          setTotal(data.total || 0);
          setTotalPages(data.totalPages || 0);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setRows([]);
          setTotal(0);
          setTotalPages(0);
          setError("Тармоқлар рўйхати юкланмади.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, voltage, page, retryKey]);

  function openNetwork(id) {
    navigate(`/networks/${id}`);
  }

  function renderPagination() {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisible = 7;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push("...");
    }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }

    return (
      <div className="sm-pagination">
        <button className="sm-pagination__btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>
          &larr;
        </button>
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`d${i}`} className="sm-pagination__dots">
              ...
            </span>
          ) : (
            <button
              key={p}
              className={`sm-pagination__btn ${p === page ? "is-active" : ""}`}
              onClick={() => setPage(p)}
            >
              {p}
            </button>
          )
        )}
        <button className="sm-pagination__btn" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
          &rarr;
        </button>
      </div>
    );
  }

  return (
    <Layout>
      <div className="sm-page sm-page--directory sm-page--networks-directory">
        <section className="sm-management-hero">
          <div className="sm-management-hero__copy">
            <p className="sm-management-hero__eyebrow">Бошқарув</p>
            <h1 className="sm-management-hero__title">Тармоқлар реестри</h1>
            <p className="sm-management-hero__text">
              Кучланиш, узунлик, сим тури ва филиал кесимидаги линиялар.
            </p>
          </div>
          <div className="sm-management-hero__metrics">
            <div className="sm-management-metric">
              <span>Жами</span>
              <strong>{loading ? "..." : total}</strong>
            </div>
            <div className="sm-management-metric">
              <span>Саҳифа</span>
              <strong>{totalPages ? `${page}/${totalPages}` : "0/0"}</strong>
            </div>
          </div>
        </section>

        <div className="sm-page__header sm-directory-header">
          <PageSwitcher active="networks" />
          <div className="sm-directory-actions">
            <button className="sm-btn sm-btn--ghost" onClick={() => setShowDeleted(true)}>
              Ўчирилганлар
            </button>
            {canCreate && (
              <button className="sm-btn sm-btn--primary" onClick={() => navigate("/networks/new")}>
                Янги тармоқ
              </button>
            )}
          </div>
        </div>

        <section className="sm-directory-filters" aria-label="Network filters">
          <div className="sm-search sm-search--directory">
            <span className="sm-search__icon" aria-hidden="true">
              &#128269;
            </span>
            <input
              className="sm-search__input"
              aria-label="Тармоқ қидириш"
              placeholder="Қидириш..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="sm-pills sm-pills--directory" aria-label="Voltage filter">
            <button className={`sm-pill ${!voltage ? "is-active" : ""}`} onClick={() => setVoltage("")}>
              Барчаси
            </button>
            {VOLTAGES.map((v) => (
              <button
                key={v}
                className={`sm-pill ${voltage === v ? "is-active" : ""}`}
                onClick={() => setVoltage(voltage === v ? "" : v)}
              >
                {v} кВ
              </button>
            ))}
          </div>
          {!loading && <span className="sm-directory-total">Жами: {total}</span>}
        </section>

        {error ? (
          <div className="sm-state sm-state--error">
            <strong>{error}</strong>
            <button type="button" className="sm-btn sm-btn--primary" onClick={() => setRetryKey((v) => v + 1)}>
              Қайта уриниш
            </button>
          </div>
        ) : loading ? (
          <div className="sm-loading">
            <div className="sm-spinner" />
            Юкланмоқда...
          </div>
        ) : (
          <>
            <div className="sm-table-wrap sm-table-wrap--directory">
              <table className="sm-table">
                <thead>
                  <tr>
                    <th>Номи</th>
                    <th>Кучланиш</th>
                    <th>Узунлик</th>
                    <th>Сим тури</th>
                    <th>Ишга туш. йили</th>
                    <th>Филиал</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="sm-table__empty">
                        {hasFilters ? "Фильтр бўйича тармоқлар топилмади" : "Тармоқлар топилмади"}
                      </td>
                    </tr>
                  ) : (
                    rows.map((r) => (
                      <tr key={r.id} onClick={() => openNetwork(r.id)}>
                        <td className="sm-table__name">{r.name}</td>
                        <td>
                          <span className="sm-badge sm-badge--voltage">{r.voltage || "—"}</span>
                        </td>
                        <td>{r.length || "—"}</td>
                        <td>{r.wire_type || "—"}</td>
                        <td>{r.start_year || "—"}</td>
                        <td>{r.city ? <span className="sm-badge sm-badge--branch">{r.city.name}</span> : "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="sm-mobile-list" aria-label="Networks">
              {rows.length === 0 ? (
                <div className="sm-state sm-state--empty">
                  {hasFilters ? "Фильтр бўйича тармоқлар топилмади" : "Тармоқлар топилмади"}
                </div>
              ) : (
                rows.map((r) => (
                  <article
                    className="sm-mobile-card"
                    key={r.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openNetwork(r.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") openNetwork(r.id);
                    }}
                  >
                    <div className="sm-mobile-card__top">
                      <strong>{r.name}</strong>
                      <span className="sm-badge sm-badge--voltage">{r.voltage || "—"}</span>
                    </div>
                    <div className="sm-mobile-card__grid">
                      <span>Узунлик</span>
                      <b>{r.length || "—"}</b>
                      <span>Сим</span>
                      <b>{r.wire_type || "—"}</b>
                      <span>Филиал</span>
                      <b>{r.city?.name || "—"}</b>
                    </div>
                    <p className="sm-mobile-card__address">Ишга тушган йили: {r.start_year || "—"}</p>
                  </article>
                ))
              )}
            </div>

            {renderPagination()}
          </>
        )}

        {showDeleted && <DeletedLogModal onClose={() => setShowDeleted(false)} apiUrl="/api/network-management/deleted/log" />}
      </div>
    </Layout>
  );
}
